import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Logo, { BRAND_TYPES, LOGO_TYPES } from '@stubhub/react-logo';
import NavFilter from '@stubhub/react-nav-filter';
import SearchBar from '@stubhub/react-search-component';
import { connect } from '@stubhub/react-store-provider';

import FEATURE_FLAGS from '../../../utils/featureFlags';

import getNavConfig from './nav-config';
import controller from './nav-controller';
import NavMenu from './nav-menu';
import messages from './nav-messages';

import '../styles/nav.scss';

/* istanbul ignore next*/
const NavItemGroup = ({
  id,
  isSellLanding,
  items,
  activeItem,
  onItemClick,
  onOptionClick,
  onClickOutside,
  onMenuHover,
  onCategoryHover,
  screenSize,
}) => {
  return (
    <ul className={`mh__nav-item-group mh__nav-item-group__${id}`}>
      {items.map((menu) => {
        return (
          <li className={`mh__nav-item mh__nav-item-${menu.id}`} key={menu.id}>
            <NavMenu
              {...menu}
              className={menu.key}
              isSellLanding={isSellLanding}
              onItemClick={onItemClick}
              onOptionClick={onOptionClick}
              onClickOutside={onClickOutside}
              onMenuHover={onMenuHover}
              onCategoryHover={onCategoryHover}
              active={menu.id === activeItem}
              screenSize={screenSize}
            />
          </li>
        );
      })}
    </ul>
  );
};

export class HeaderNav extends React.Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  constructor(props, context) {
    super(props);
    this.state = {
      showLanguageSelector: props.showLanguageSelector || false,
      isNavMenuActive: false,
      activeMenuKey: null,
      activeMenuItems: [],
    };
    const { globalRegistry } = context;
    this.enableSubCatNav =
      globalRegistry &&
      globalRegistry.getFeatureValue('common.enableSubCatNav');
  }

  formatMessage(item, intl) {
    item.label = intl.formatMessage(messages[item.id]);
    if (messages[`${item.id}.toggle`]) {
      item.toggleLabel = intl.formatMessage(messages[`${item.id}.toggle`]);
    }
    if (item.items) {
      item.items.forEach((sub) => {
        this.formatMessage(sub, intl);
      });
    }
  }

  resetNavMenuState() {
    this.setState({
      isNavMenuActive: false,
      activeMenuKey: null,
      activeMenuItems: [],
    });

    this.props.onNavMenuStateChange(false);
  }

  updateNavMenuState(key, items) {
    if (this.state.activeMenuKey == null || this.state.activeMenuKey !== key) {
      /*
       * No active key currently, or the current active key is different,
       * need to make new key active
       */
      this.setState({
        isNavMenuActive: true,
        activeMenuKey: key,
        activeMenuItems: items,
      });

      this.props.onNavMenuStateChange(true);
    } else {
      // The key is currently active, make it inactive
      this.resetNavMenuState();
    }
  }

  clearUserAuth = () => {
    const domain = window.location.host.split('www').pop();

    return this.props.signOutUser(domain).then(() => {
      // Console logs showed that this line is executing, but coverage may have issue with the promise
      /* istanbul ignore next */
      this.resetNavMenuState();

      return '';
    });
  };

  onItemClick = (key, items) => {
    if (key === 'search') {
      /**
       * Handle Search Button Click
       *
       */
      const { showSearchBarModal } = this.props;
      showSearchBarModal && /* istanbul ignore next */ showSearchBarModal(); // Ignore Defensive Coding for `showSearchBarModal` Function.
      this.resetNavMenuState();
    } else if (items) {
      // In these cases, there are nav menu items, we need to show/hide them
      this.updateNavMenuState(key, items);
    }
  };

  onOptionClick = (key) => {
    if (key === 'logout') {
      this.clearUserAuth();
    }

    // Without this check, the mweb sell dropdown menu resets the nav state before it can expand
    if (key === 'sell' && this.props.screenSize === 'small') {
      return;
    }

    // Doesn't matter what option was clicked, reset nav menu state
    this.resetNavMenuState();
  };

  onClickOutside = () => {
    // Reset nav menu state
    this.resetNavMenuState();
  };

  onMenuHover = (key, items) => {
    if (
      (!this.state.activeMenuKey || this.state.activeMenuKey == null) &&
      key === 'sell' &&
      items
    ) {
      this.setState({
        isNavMenuActive: true,
        activeMenuKey: key,
        activeMenuItems: items,
      });
    }
  };

  // Need subCatNav feature toggle to ensure hover doesn't get stuck on category when feature off
  onCategoryHover = (key) => {
    if (
      this.enableSubCatNav &&
      (!this.state.activeMenuKey || this.state.activeMenuKey == null) &&
      key !== 'gift-cards'
    ) {
      this.setState({
        isNavMenuActive: true,
        activeMenuKey: key,
      });
    }
  };

  render() {
    const {
      end,
      homepage,
      intl,
      isModalSearchActive,
      isSellLanding,
      langSelector,
      noNavSecondary,
      onModalSearchActiveChanged,
      onlyNavSecondary,
      screenSize,
      searchpage,
      start,
      searchBarStyle,
      logoType,
    } = this.props;

    const { globalRegistry } = this.context;
    const locale = globalRegistry.getCurrentLocale();
    const isSessionAuthEnabled = globalRegistry.getFeatureValue(
      FEATURE_FLAGS.common.auth.sessionAuthEnabled
    );
    const useAltBrand = globalRegistry.getFeatureValue(
      FEATURE_FLAGS.common.altBrand.useAltBrand
    );
    const brandType = useAltBrand
      ? BRAND_TYPES.ALTERNATIVE
      : BRAND_TYPES.DEFAULT;
    const data = getNavConfig(
      globalRegistry.getStoreId(),
      locale,
      isSessionAuthEnabled
    );
    const { discovery, actions } = data;

    /**
     * Add search button, if needed, to appropriate menu
     *
     */
    const searchNavItem = {
      id: 'search',
      type: 'menu',
    };

    if (this.props.searchBarStyle === 'modal') {
      // Need to add search button
      if (screenSize === 'small') {
        actions.unshift(searchNavItem);
      }
    }

    // Add labels
    for (const key in data) {
      const group = data[key];
      group.forEach((item) => {
        this.formatMessage(item, intl);
      });
    }

    const { activeMenuKey } = this.state;

    const navTop = !onlyNavSecondary ? (
      <nav
        className={`mh__nav-top ${
          isModalSearchActive ? 'mh__nav--searchActive' : ''
        }`}
      >
        <div className="mh__nav-primary">
          <Logo
            data-testid="logo_merch"
            className="logo_merch"
            onItemClick={this.onItemClick}
            type={logoType}
            brand={brandType}
          />
          {!this.props.isModalSearchActive && (
            <NavItemGroup
              id="actions"
              items={actions}
              activeItem={activeMenuKey}
              onItemClick={this.onItemClick}
              onOptionClick={this.onOptionClick}
              onClickOutside={this.onClickOutside}
              onMenuHover={this.onMenuHover}
              screenSize={screenSize}
              isSellLanding={isSellLanding}
            />
          )}
        </div>
      </nav>
    ) : null;

    const navSearch = !onlyNavSecondary ? (
      <div className="mh__nav-search">
        <TransitionGroup>
          {
            /**
             * Render the Search Component
             * NOTE - This renders the search bar in either mode
             *
             */
            <CSSTransition
              classNames="HeaderSearchBar"
              timeout={{ enter: 500, exit: 500 }}
            >
              <SearchBar
                app="sell"
                searchStyle={searchBarStyle}
                onModalSearchActiveChanged={onModalSearchActiveChanged}
                suggestVariant={7}
                merchClassName="MerchHeader"
              />
            </CSSTransition>
          }
        </TransitionGroup>
      </div>
    ) : null;

    let navSecondary = null;
    if (
      !noNavSecondary &&
      !(!(homepage || langSelector || searchpage) && screenSize === 'small')
    ) {
      const navFilter = (
        <NavFilter
          {...{
            className: 'mh__nav-secondary',
            globalRegistry,
            screenSize,
            homepage,
            langSelector,
            searchpage,
            noDateSelector: !noNavSecondary,
            start,
            end,
          }}
        />
      );
      navSecondary = (
        <div className="mh__nav-secondary">
          {!this.props.isModalSearchActive && (
            <NavItemGroup
              id="discovery"
              items={discovery}
              activeItem={activeMenuKey}
              onItemClick={this.onItemClick}
              onOptionClick={this.onOptionClick}
              onClickOutside={this.onClickOutside}
              onCategoryHover={this.onCategoryHover}
              screenSize={screenSize}
              isSellLanding={isSellLanding}
            />
          )}
          {navFilter}
        </div>
      );
    }

    return (
      <div className="mh__nav-container">
        <div className="mh__nav-wrapper">
          {navTop}
          {navSearch}
          {navSecondary}
        </div>
      </div>
    );
  }
}

HeaderNav.propTypes = {
  showSearchBarModal: PropTypes.func,
  searchBarStyle: PropTypes.string.isRequired,
  isModalSearchActive: PropTypes.bool,
  isSellLanding: PropTypes.bool,
  screenSize: PropTypes.string,
  onModalSearchActiveChanged: PropTypes.func,
  logoType: PropTypes.oneOfType(LOGO_TYPES),
};

HeaderNav.defaultProps = {
  searchBarStyle: 'expanded',
  isModalSearchActive: false,
  screenSize: 'large',
};

export default connect(controller)(injectIntl(HeaderNav));
